// components/authentication/LoginForm.js - Formulario de login limpio
import React, { useState } from 'react';
import { Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faSignInAlt, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { getLoginRedirectPath, getClientConfig } from '../../utils/clientDomain';

const LoginForm = ({ hasLabel = true, layout = 'split' }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    remember: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const clientConfig = getClientConfig();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Limpiar error cuando el usuario empiece a escribir
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (isSubmitting) return;

    setError('');
    setIsSubmitting(true);

    try {
      const result = await login({
        username: formData.username,
        password: formData.password,
        remember: formData.remember
      });
      
      if (result.success) {
        // Determinar el mensaje de bienvenida basado en el subdominio
        const welcomeMessage = clientConfig?.isClientDomain 
          ? `¡Bienvenido a tu Dashboard, ${formData.username}!`
          : `¡Bienvenido a Galaxy Metrics, ${formData.username}!`;
        
        toast.success(welcomeMessage, {
          theme: 'colored'
        });
        
        // Determinar ruta de redirección basada en el subdominio y rol del usuario
        let redirectPath = location.state?.from?.pathname;
        
        if (!redirectPath || redirectPath === '/login') {
          // Obtener el usuario actualizado del contexto después del login
          // Nota: el usuario se actualiza en el contexto después del login exitoso
          // Necesitamos esperar un momento para que el contexto se actualice
          setTimeout(() => {
            const userFromContext = result.user || user;
            redirectPath = getLoginRedirectPath(userFromContext);
            navigate(redirectPath, { replace: true });
          }, 100);
          return; // Salir para evitar la navegación inmediata
        }
        
        navigate(redirectPath, { replace: true });
      } else {
        // Mostrar error en el formulario Y notificación toast
        const errorMessage = result.error || 'Credenciales incorrectas';
        setError(errorMessage);
        toast.error(errorMessage, {
          theme: 'colored',
          autoClose: 5000
        });
      }
    } catch (error) {
      const errorMessage = 'Error inesperado. Inténtalo de nuevo.';
      setError(errorMessage);
      toast.error(errorMessage, {
        theme: 'colored',
        autoClose: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Form 
      onSubmit={handleSubmit}
      noValidate
    >
      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-3">
          <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
          {error}
        </Alert>
      )}

      {/* Username Field */}
      <Form.Group className="mb-3">
        {hasLabel && (
          <Form.Label htmlFor="username">
            Usuario <span className="text-danger">*</span>
          </Form.Label>
        )}
        <Form.Control
          id="username"
          type="text"
          name="username"
          placeholder={!hasLabel ? 'Usuario' : ''}
          value={formData.username}
          onChange={handleChange}
          disabled={isSubmitting}
          required
          autoComplete="username"
          className={error ? 'is-invalid' : ''}
        />
      </Form.Group>

      {/* Password Field */}
      <Form.Group className="mb-3">
        {hasLabel && (
          <Form.Label htmlFor="password">
            Contraseña <span className="text-danger">*</span>
          </Form.Label>
        )}
        <div className="position-relative">
          <Form.Control
            id="password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder={!hasLabel ? 'Contraseña' : ''}
            value={formData.password}
            onChange={handleChange}
            disabled={isSubmitting}
            required
            autoComplete="current-password"
            className={error ? 'is-invalid' : ''}
          />
          <Button
            type="button"
            variant="outline-secondary"
            className="position-absolute end-0 top-0 h-100 d-flex align-items-center justify-content-center"
            style={{ width: '40px', border: 'none', background: 'transparent' }}
            onClick={togglePasswordVisibility}
            disabled={isSubmitting}
            tabIndex={-1}
          >
            <FontAwesomeIcon 
              icon={showPassword ? faEyeSlash : faEye} 
              className="text-muted"
            />
          </Button>
        </div>
      </Form.Group>

      {/* Remember Me */}
      <Row className="justify-content-between align-items-center mb-3">
        <Col xs="auto">
          <Form.Check
            type="checkbox"
            id="remember"
            name="remember"
            label="Recordarme"
            checked={formData.remember}
            onChange={handleChange}
            disabled={isSubmitting}
          />
        </Col>
        <Col xs="auto">
          <Link
            to="/forgot-password"
            className="fs-9 fw-semi-bold text-decoration-none"
            style={{ fontSize: '0.85rem' }}
          >
            ¿Olvidaste tu contraseña?
          </Link>
        </Col>
      </Row>

      {/* Submit Button */}
      <div className="mb-3">
        <Button
          type="submit"
          variant="primary"
          className="w-100 d-flex align-items-center justify-content-center"
          disabled={isSubmitting || !formData.username || !formData.password}
          style={{ height: '48px' }}
        >
          {isSubmitting ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
              Iniciando sesión...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faSignInAlt} className="me-2" />
              Iniciar sesión
            </>
          )}
        </Button>
      </div>

    </Form>
  );
};

LoginForm.propTypes = {
  hasLabel: PropTypes.bool,
  layout: PropTypes.oneOf(['simple', 'split', 'card'])
};

export default LoginForm; 